import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, ShieldPlus } from 'lucide-react';
import {
  Department,
  UserRole,
  createPrivilegedUserBodySchema,
  departmentLabelKey,
  departmentSchema,
  suggestedDepartmentForRole,
  userRoleSchema,
  type TempPasswordResponse,
} from '@nbh/shared';
import { adminApi } from '../../lib/adminApi';
import { ROLE_LABEL_KEY } from './SuperAdminDialogs';

/**
 * Creates a privileged account.
 *
 * Unlike the ordinary admin form this one offers every role, SUPER_ADMIN included —
 * `POST /api/super-admin/users` is the only route that may mint one (D23).
 */
export function CreatePrivilegedUserForm({
  onCreated,
  onError,
}: {
  onCreated: (created: TempPasswordResponse) => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.Admin);
  const [department, setDepartment] = useState<Department>(Department.Administration);

  /** Pre-selects the obvious team, leaving it free: an ADMIN may sit in MAINTENANCE (D22). */
  function chooseRole(next: UserRole) {
    setRole(next);
    const suggestion = suggestedDepartmentForRole[next];
    if (suggestion) setDepartment(suggestion);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    // Parsed against the shared contract rather than checked by hand, so the email is
    // trimmed and lowercased here exactly as the server will store it.
    const parsed = createPrivilegedUserBodySchema.safeParse({ name, email, role, department });
    if (!parsed.success) {
      onError(t('superAdmin.staff.invalid'));
      return;
    }

    try {
      onCreated(await adminApi.superAdmin.users.create(parsed.data));
      setName('');
      setEmail('');
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : t('superAdmin.failed'));
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-800 p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium text-slate-300">
        <ShieldPlus className="size-3.5" aria-hidden="true" />
        {t('superAdmin.staff.addHeading')}
      </p>
      <p className="mb-2 mt-1 text-xs text-slate-500">{t('superAdmin.staff.addExplain')}</p>

      <div className="flex flex-wrap gap-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('superAdmin.staff.name')}
          aria-label={t('superAdmin.staff.name')}
          className="min-w-[120px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t('superAdmin.staff.email')}
          aria-label={t('superAdmin.staff.email')}
          className="min-w-[140px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <select
          value={role}
          onChange={(event) => chooseRole(event.target.value as UserRole)}
          aria-label={t('superAdmin.staff.role')}
          className="min-h-[48px] rounded border border-slate-700 bg-slate-900 px-2 text-sm"
        >
          {userRoleSchema.options.map((option) => (
            <option key={option} value={option}>
              {t(ROLE_LABEL_KEY[option])}
            </option>
          ))}
        </select>
        <select
          value={department}
          onChange={(event) => setDepartment(event.target.value as Department)}
          aria-label={t('superAdmin.staff.department')}
          className="min-h-[48px] rounded border border-slate-700 bg-slate-900 px-2 text-sm"
        >
          {departmentSchema.options.map((option) => (
            <option key={option} value={option}>
              {t(departmentLabelKey[option])}
            </option>
          ))}
        </select>
      </div>

      <button
        type="submit"
        disabled={!name.trim() || !email.trim()}
        className="mt-2 min-h-[48px] w-full rounded-lg bg-brookfields-green ring-1 ring-brookfields-gold/70 text-sm font-semibold text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
      >
        {t('superAdmin.staff.add')}
      </button>
    </form>
  );
}

/**
 * Shown once. Only the hash is stored, so it cannot be looked up again — if it is
 * lost before it reaches the person, reset it from the ordinary staff screen.
 */
export function TempPasswordNotice({
  credential,
  onDismiss,
}: {
  credential: TempPasswordResponse;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border-2 border-amber-500/60 bg-amber-950/30 p-3">
      <p className="text-xs text-amber-200">
        {t('superAdmin.staff.tempOnce', { email: credential.email })}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="rounded bg-slate-950 px-2 py-1 font-mono text-sm text-amber-100">
          {credential.temporaryPassword}
        </code>
        <button
          type="button"
          onClick={() => void navigator.clipboard?.writeText(credential.temporaryPassword)}
          className="inline-flex min-h-[48px] items-center gap-1 rounded bg-slate-800 px-3 text-xs text-slate-200"
        >
          <Copy className="size-3.5" aria-hidden="true" />
          {t('superAdmin.staff.copy')}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="min-h-[48px] rounded bg-transparent px-3 text-xs text-slate-400"
        >
          {t('superAdmin.staff.dismiss')}
        </button>
      </div>
    </div>
  );
}
